import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import gtm from 'general/js/gtm';
import ScoringLoader, { endpointNames } from 'general/js/loaders/scoring-loader';
import {
    getRoundById,
    isRoundChoicesEqual,
    isRoundChoiceAvailable,
    setSelectedChoice,
} from 'general/js/helpers';
import { BlockPlaceholder } from 'components/block-placeholder';
import { BlockSpinner } from 'components/block-spinner';
import { ReactModal } from 'components/modal';
import { bemClasses, LivePreMessage } from 'components/live-pre-message';
import LiveStatsBar from './live-stats-bar';
import LiveStatsList from './live-stats-list';
import LiveStatsCardContainer from './live-stats-card-container';
import { getStatsCards, typeChoices } from './enums';

class LiveStatsLayout extends React.Component {
    constructor(props) {
        super(props);

        const typeChoice = typeChoices.find((round) => round.selected === true);
        const selectedType = typeChoice ? typeChoice.value : 'all';

        this.state = {
            roundChoices: null,
            selectedRoundId: null,
            hasUserSelected: false,
            selectedType,
            activeCardName: '',
            isModalOpen: false,
            shouldAnimated: true,
            filters: ''
        };

        this.state.filters = this.getFilter(this.state.selectedRoundId, this.state.selectedType);
    }

    componentDidMount() {
        ScoringLoader.subscribe(endpointNames.LIVE_SCANNER);
        ScoringLoader.subscribe(endpointNames.STATISTICS);
        this.updateChoices();
    }

    componentDidUpdate() {
        this.updateChoices();
    }

    componentWillUnmount() {
        ScoringLoader.unsubscribe(endpointNames.LIVE_SCANNER);
        ScoringLoader.unsubscribe(endpointNames.STATISTICS);
    }

    getAvailableChoices() {
        const { liveScanner, scoringStats } = this.props;
        const roundChoices = get(liveScanner, 'liveComponents.stats.roundChoices');

        if (!scoringStats || !Array.isArray(scoringStats.rounds)) return [];

        return cloneDeep(roundChoices).filter((choice) => {
            return scoringStats.rounds.find((round) =>
                round.id.toString() === choice.value.toString());
        });
    }

    updateChoices() {
        const { liveScanner, scoringStats } = this.props;
        const { selectedRoundId } = this.state;

        if (!liveScanner || !scoringStats) return;

        const prevChoices = this.state.roundChoices;
        const roundChoices = this.getAvailableChoices();

        if (this.state.hasUserSelected &&
            isRoundChoiceAvailable(roundChoices, selectedRoundId)
        ) {
            setSelectedChoice(roundChoices, selectedRoundId);

            if (!isRoundChoicesEqual(prevChoices, roundChoices)) {
                this.setState({ roundChoices });
            }
            return;
        }

        const foundChoice = roundChoices.find((choice) => choice.selected === true);

        if (!foundChoice && roundChoices.length) {
            const lastChoice = roundChoices[roundChoices.length - 1];

            lastChoice.selected = true;
            this.setState({
                roundChoices,
                selectedRoundId: lastChoice.value,
                hasUserSelected: true,
            });
            return;
        }

        if (isRoundChoicesEqual(prevChoices, roundChoices)) return;

        this.setState({
            roundChoices,
            selectedRoundId: (foundChoice && foundChoice.value) || null,
            hasUserSelected: false,
        });
    }

    getStatCardsByType(type = 'all') {
        const statsCards = getStatsCards();

        if (type === 'all') return statsCards;

        return statsCards.filter((statCard) => statCard.type === type);
    }

    generateCardList(cardList, round, scoringStats) {
        if (!round) return [];

        return cardList.map((statsCard) => Object.assign(
            statsCard,
            {
                stats: round[statsCard.name].stats,
                sitecoreData: cloneDeep(scoringStats.sitecoreData[statsCard.name])
            }
        ));
    }

    getFilter(selectedRoundId, selectedType) {
        return `${selectedRoundId}, ${selectedType}`;
    }

    setRound = (roundId) => {
        gtm.push(
            'Statistics',
            'Dropdown',
            `Round ${roundId} > ${window.location.href}`
        );
        this.setState({
            selectedRoundId: roundId,
            hasUserSelected: true,
            filters: this.getFilter(roundId, this.state.selectedType),
        });
    }

    setType = (type) => {
        gtm.push(
            'Statistics',
            'Dropdown',
            `${type} > ${window.location.href}`
        );
        this.setState({
            selectedType: type,
            filters: this.getFilter(this.state.selectedRoundId, type)
        });
    }

    showCardDetails = (statName) => {
        this.setState({
            isModalOpen: true,
            activeCardName: statName,
        });
    }

    hideCardDetails = () => {
        this.setState({
            isModalOpen: false,
            activeCardName: '',
        });
    }

    getScoringRound() {
        const { scoringStats } = this.props;

        if (!scoringStats) return null;

        return getRoundById(scoringStats.rounds, this.state.selectedRoundId);
    }

    render() {
        const { liveScanner, scoringStats } = this.props;

        if (liveScanner === undefined || scoringStats === undefined) {
            return (
                <BlockPlaceholder minHeight={300}>
                    <BlockSpinner />;
                </BlockPlaceholder>
            );
        }

        const activeRound = this.getScoringRound();
        const config = get(liveScanner, 'liveComponents.stats');

        if (!config.show || !scoringStats || !activeRound) {
            const messageModel = {
                title: config.title,
                body: config.subtitle,
                textColor: config.textColor
            };

            return <LivePreMessage model={messageModel} bemList={[bemClasses.LIGHT]} />;
        }

        const cardList = this.generateCardList(
            this.getStatCardsByType(this.state.selectedType),
            activeRound,
            scoringStats
        );

        let activeCard = null;

        if (this.state.activeCardName) {
            activeCard = cardList.find((card) => card.name === this.state.activeCardName) || null;
        }

        return (
            <div className="live-stats">
                <LiveStatsBar
                    choices={{ roundChoices: this.state.roundChoices, typeChoices }}
                    setRound={this.setRound}
                    setType={this.setType}
                />
                <LiveStatsList
                    cardList={cardList}
                    shouldAnimated={this.state.shouldAnimated}
                    filters={this.state.filters}
                    showAll={this.showCardDetails}
                />
                <ReactModal isOpen={this.state.isModalOpen} onCloseCb={this.hideCardDetails} closeModal={false}>
                    {
                        this.state.isModalOpen &&
                        <LiveStatsCardContainer
                            model={activeCard}
                            showPlayerCard={this.showPlayerCard}
                            isFull
                        />
                    }
                </ReactModal>
            </div>
        );
    }
}

LiveStatsLayout.propTypes = {
    options: PropTypes.object
};

const mapStateToProps = (state) => ({
    liveScanner: state.scoring[endpointNames.LIVE_SCANNER],
    scoringStats: state.scoring[endpointNames.STATISTICS],
    scoringCourse: state.scoring[endpointNames.COURSE],
});

const LiveStatsLayoutContainer = connect(
    mapStateToProps,
    null
)(LiveStatsLayout);

export default LiveStatsLayoutContainer;
